import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import "../styles/globals.css";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ReactGA from "react-ga4";
import App from "next/app";
import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import NextNProgress from "nextjs-progressbar";
import Polish from "../lang/compiled/pl.json";
import English from "../lang/compiled/en.json";
import { IntlProvider } from "react-intl";
import { Toaster } from "react-hot-toast";
import { SessionProvider } from "next-auth/react";
import { Hydrate, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { defaultLocale } from "../config";
import { ConfigProvider } from "../modules/Config/ConfigContext";
import { infoToast } from "../modules/Toast";
import { InitialModalProvider } from "../modules/InitialModal/InitialModalProvider";
import { envGetOrThrow } from "../utils/envGetOrThrow";
import { JWPlayerProvider } from "../modules/Video/JWPlayerContext";
import { DefaultSeo } from "../utils";
import Layout from "../modules/Layout/components/Layout";
const MyApp = (param)=>{
    let { Component , pageProps , router , config , toast , initialModal  } = param;
    const [queryClient] = useState(()=>{
        return new QueryClient({
            defaultOptions: {
                queries: {
                    refetchOnMount: false,
                    retry: (failureCount, error)=>{
                        var ref, ref1;
                        if ((error === null || error === void 0 ? void 0 : (ref = error.response) === null || ref === void 0 ? void 0 : ref.status) == 502 && failureCount > 3) return false;
                        if ((error === null || error === void 0 ? void 0 : (ref1 = error.response) === null || ref1 === void 0 ? void 0 : ref1.status) >= 400) return false;
                        return true;
                    }
                }
            }
        });
    });
    const locale = router.locale;
    const [shortLocale] = locale ? locale.split("-") : [
        defaultLocale
    ];
    const messages = useMemo(()=>{
        switch(shortLocale){
            case "pl":
                return Polish;
            case "en":
                return English;
            default:
                return English;
        }
    }, [
        shortLocale
    ]);
    useEffect(()=>{
        if (!toast) return;
        infoToast({
            title: toast
        });
        const queryWithoutToast = {
            ...router.query,
            toast: undefined
        };
        delete queryWithoutToast.toast;
        void router.replace({
            href: router.route,
            query: queryWithoutToast
        });
    }, [
        toast
    ]);
    useEffect(()=>{
        ReactGA.initialize("G-K6YBTMGWHL");
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Head, {
                children: [
                    /*#__PURE__*/ _jsx("link", {
                        rel: "icon",
                        href: "/favicon.ico"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "robots",
                        content: "noindex"
                    }),
                    /*#__PURE__*/ _jsx("link", {
                        rel: "preload",
                        href: "/fonts/OfficinaSansStd-Bold.woff2",
                        as: "font",
                        type: "font/woff2",
                        crossOrigin: "anonymous"
                    }),
                    /*#__PURE__*/ _jsx("link", {
                        rel: "preload",
                        href: "/fonts/OfficinaSansStd-Book.woff2",
                        as: "font",
                        type: "font/woff2",
                        crossOrigin: "anonymous"
                    }),
                    /*#__PURE__*/ _jsx("link", {
                        rel: "preload",
                        href: "/fonts/GothamLight.woff2",
                        as: "font",
                        type: "font/woff2",
                        crossOrigin: "anonymous"
                    }),
                    /*#__PURE__*/ _jsx("link", {
                        rel: "preload",
                        href: "/fonts/Gotham-Bold.woff2",
                        as: "font",
                        type: "font/woff2",
                        crossOrigin: "anonymous"
                    }),
                    /*#__PURE__*/ _jsx("link", {
                        rel: "preload",
                        href: "/fonts/Gotham-Book.woff2",
                        as: "font",
                        type: "font/woff2",
                        crossOrigin: "anonymous"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(ConfigProvider, {
                config: config,
                children: [
                    /*#__PURE__*/ _jsx(DefaultSeo, {}),
                    /*#__PURE__*/ _jsx(NextNProgress, {
                        color: "#22c55e",
                        options: {
                            showSpinner: false
                        }
                    }),
                    /*#__PURE__*/ _jsx(SessionProvider, {
                        session: pageProps.session,
                        children: /*#__PURE__*/ _jsx(QueryClientProvider, {
                            client: queryClient,
                            children: /*#__PURE__*/ _jsx(Hydrate, {
                                state: pageProps.dehydratedState,
                                children: /*#__PURE__*/ _jsx(IntlProvider, {
                                    messages: messages,
                                    locale: shortLocale,
                                    children: /*#__PURE__*/ _jsx(InitialModalProvider, {
                                        initialModal: initialModal !== null && initialModal !== void 0 ? initialModal : null,
                                        children: /*#__PURE__*/ _jsx(JWPlayerProvider, {
                                            children: /*#__PURE__*/ _jsxs(Layout, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(Toaster, {
                                                        position: "top-right"
                                                    }),
                                                    /*#__PURE__*/ _jsx(Component, {
                                                        ...pageProps
                                                    })
                                                ]
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                ]
            })
        ]
    });
};
let config = null;
MyApp.getInitialProps = async (ctx)=>{
    const appProps = await App.getInitialProps(ctx);
    config = config !== null && config !== void 0 ? config : {
        pageUrl: envGetOrThrow("PAGE_URL"),
        wordpressUrl: envGetOrThrow("WORDPRESS_URL"),
        algoliaConfig: {
            indexName: envGetOrThrow("ALGOLIA_INDEX_NAME"),
            appId: envGetOrThrow("ALGOLIA_APP_ID"),
            apiKey: envGetOrThrow("ALGOLIA_PUBLIC_API_KEY")
        },
        streamPlaylistId: envGetOrThrow("JWPLAYER_STREAM_PLAYLIST_ID"),
        mapboxAccessToken: envGetOrThrow("MAP_BOX_TOKEN"),
        imgixConfig: {
            s3Domain: envGetOrThrow("IMGIX_DOMAIN"),
            proxyDomain: envGetOrThrow("IMGIX_JWP_DOMAIN"),
            proxySecureToken: envGetOrThrow("IMGIX_JWP_PROXY_TOKEN")
        }
    };
    const toast = getToastQueryFromCtx(ctx);
    const initialModal = getInitialModalStateFromCtx(ctx);
    return {
        ...appProps,
        config,
        toast,
        initialModal
    };
};
function getToastQueryFromCtx(ctx) {
    return ctx.ctx.query["toast"];
}
function getInitialModalStateFromCtx(ctx) {
    var ref;
    return (ref = ctx.ctx.query["openModal"]) !== null && ref !== void 0 ? ref : null;
}
export default MyApp;
